import { CommonModule } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgOptionHighlightModule } from '@ng-select/ng-option-highlight';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { BsModalService, ModalDirective, ModalModule } from 'ngx-bootstrap/modal';
import * as numeral from 'numeral';
import { Observable, Subject, concat, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, startWith, switchMap, take, tap } from 'rxjs/operators';

import { PreventDoubleClickDirective } from '@shared/directives/prevent-double-click.directive';
import { NotificationTypeEnum } from '@shared/enum/notification-type.enum';
import { RequestPageModesEnum } from '@shared/enum/request-step.enum';
import { AlertModalComponent } from '@shared/layouts';
import { ChildItem } from '@shared/layouts/modals/full-modal/child-item';
import { FullModalComponent } from '@shared/layouts/modals/full-modal/full-modal.component';
import { ErrorResponse, MerchantSearch } from '@shared/models';
import { AuthGuardService } from '@shared/services';
import { StoreRequestService } from '@shared/services/store-request.service';
import { StoreService } from '@shared/services/store.service';

import { StoreRequestComponent } from '../../store-request/store-request.component';

@Component({
  selector: 'app-store-create-button',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ModalModule,
    NgSelectModule,
    NgOptionHighlightModule,
    TranslateModule,
    PreventDoubleClickDirective
  ],
  templateUrl: './store-create-button.component.html'
})
export class StoreCreateButtonComponent extends OnDestroyMixin {
  @ViewChild('modalCreateNewStore') modalCreateNewStore: ModalDirective;
  @ViewChild('selectMerchant') selectMerchant: NgSelectComponent;

  public merchantForm = new FormGroup({
    merchant: new FormControl<string>(null)
  });

  public merchantList: Observable<MerchantSearch>;
  public merchantSearchLoading: boolean;
  public merchantSearchInput$ = new Subject<string>();
  public numberFormat = '0,0';

  constructor(
    protected readonly modalService: BsModalService,
    protected readonly translate: TranslateService,
    protected authGuardService: AuthGuardService,
    protected storeRequestService: StoreRequestService,
    protected storeService: StoreService,
    private readonly router: Router
  ) {
    super();
  }

  hasCreatePermission(): boolean {
    return this.authGuardService.checkPermission(['store_new_m']);
  }

  onSelectMerchant() {
    this.storeService
      .getValidateLimitStore()
      .pipe(take(1), untilComponentDestroyed(this))
      .subscribe({
        next: res => {
          if (res.status === 'FAILED') {
            this.alertErrorModal(res);
          } else {
            this.loadMerchant('');
            this.modalCreateNewStore.show();
          }
        }
      });
  }

  hideModalCreateNewStore() {
    this.merchantForm.controls.merchant.patchValue(null);
    this.selectMerchant.itemsList.unmarkItem();
    this.modalCreateNewStore.hide();
  }

  loadMerchant(initialTerm: string) {
    this.merchantList = concat(
      of([]),
      this.merchantSearchInput$.pipe(
        startWith(initialTerm),
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => (this.merchantSearchLoading = true)),
        switchMap(term =>
          this.storeRequestService.searchMerchantByName({ searchCriteria: term }).pipe(
            catchError(() => of([])),
            tap(() => {
              this.merchantSearchLoading = false;
            })
          )
        )
      )
    );
  }

  goToCreate() {
    const childItem = new ChildItem(
      StoreRequestComponent,
      {
        title: 'Create Store Request',
        mode: RequestPageModesEnum.REQUEST_CREATE,
        storeId: null,
        merchant: this.merchantForm.controls.merchant.value
      },
      false
    );

    childItem.notifyParent.pipe(untilComponentDestroyed(this)).subscribe(emit => {
      if (emit && emit.notificationType === NotificationTypeEnum.NEXT && emit.result) {
        this.router.navigate(['/merchant-store/store/store-request-list']);
      }
    });

    this.modalService.show(FullModalComponent, {
      animated: false,
      backdrop: false,
      initialState: { title: null, childItem }
    });

    this.hideModalCreateNewStore();
  }

  alertErrorModal(errorResponse: ErrorResponse & { limitQuotaStore?: number }) {
    const initialState = {
      title: 'Failed',
      message:
        errorResponse.code === '07021'
          ? this.translate.instant('ERROR_CODE.' + errorResponse.code + '.store', {
              limitQuotaStore: numeral(errorResponse.limitQuotaStore).format(this.numberFormat)
            })
          : this.translate.instant(errorResponse.translateKey, { context: errorResponse.message })
    };

    this.modalService.show(AlertModalComponent, {
      initialState
    });
  }
}
